import React from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, X } from "lucide-react";

/**
 * Apple-styled "Are you sure?" confirmation modal.
 *
 * Usage (with the `useConfirm` hook below — recommended):
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title: "Delete this lead?", description: "...", tone: "danger" });
 *   if (ok) doDelete();
 */
export default function ConfirmDialog({
  open,
  title = "Are you sure?",
  description = "This action cannot be undone.",
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  tone = "danger",
  onConfirm,
  onCancel,
}) {
  if (!open) return null;
  const accent = tone === "danger"
    ? "bg-rose-500 hover:bg-rose-400 text-white"
    : "bg-[#007AFF] hover:bg-[#3596ff] text-white";
  const ring = tone === "danger" ? "ring-rose-400/30" : "ring-[#007AFF]/30";

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md animate-in fade-in duration-150"
      onClick={onCancel}
      data-testid="confirm-overlay"
    >
      <div
        className={`relative w-full max-w-md rounded-3xl border border-white/[0.08] bg-[#0a0a0a]/95 shadow-2xl shadow-black/60 ring-1 ${ring} backdrop-blur-2xl animate-in zoom-in-95 duration-150`}
        onClick={(e) => e.stopPropagation()}
        data-testid="confirm-dialog"
      >
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 text-white/40 hover:text-white p-1 rounded-lg hover:bg-white/[0.06] transition"
          data-testid="confirm-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-7">
          <div className="flex items-start gap-4 mb-5">
            <div className={`shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center ${tone === "danger" ? "bg-rose-500/15" : "bg-[#007AFF]/15"}`}>
              <AlertTriangle className={`w-5 h-5 ${tone === "danger" ? "text-rose-400" : "text-[#007AFF]"}`} />
            </div>
            <div className="flex-1 pt-0.5">
              <h2 className="font-display text-xl font-light text-white leading-tight" data-testid="confirm-title">{title}</h2>
              <p className="text-sm text-white/55 mt-1.5 leading-relaxed" data-testid="confirm-description">{description}</p>
            </div>
          </div>

          <div className="flex justify-end gap-2.5 mt-7">
            <button
              onClick={onCancel}
              className="px-5 py-2.5 rounded-full text-sm bg-white/[0.06] hover:bg-white/[0.10] text-white border border-white/[0.08] transition"
              data-testid="confirm-cancel-btn"
            >
              {cancelLabel}
            </button>
            <button
              onClick={onConfirm}
              className={`px-5 py-2.5 rounded-full text-sm transition shadow-lg shadow-black/40 ${accent}`}
              data-testid="confirm-ok-btn"
              autoFocus
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
